import {
  cleanVal,
  dd,
  df,
  dfzero,
  FeeContext,
  fmtAccount,
  getModelDescription,
  getModelRisk,
  getStdParams,
  setFeesAsParam,
} from "./buildDocumentProductionList";
import {
  QueryModelPosition,
  QueryProfile,
  QuerySecurity,
} from "@/api/queries/types";
import { Contact, Portfolio } from "@/api/types";
import { BuyOrderRow } from "@/forms/forms/orders/orderForm/components/BuyOrders";
import { SellOrderRow } from "@/forms/forms/orders/orderForm/components/SellOrders";
import { SERVICE_TYPE_LABELS } from "@/forms/forms/orders/orderForm/OrderForm.options";
import { PendingOrder } from "@/forms/forms/quality/suitability/SuitabilityForm.types";
import { DocProducerDocument } from "@/views/docproducer";

// ---------------------------------------------------------------------------
// Output type
// ---------------------------------------------------------------------------

export type DocumentWithVariables = {
  /** Source DocProducer document id */
  id: string;
  /** docType of the source document, e.g. "order", "meetingMinutes" */
  type: string;
  /** Every variable belonging to this document, collected in one place */
  data: Record<string, unknown>;
};

export type DocumentListWithVariables = {
  standardParams: Record<string, string>;
  documents: DocumentWithVariables[];
};

// ---------------------------------------------------------------------------
// Order variable collection
// ---------------------------------------------------------------------------

/**
 * Gathers every variable an "order" document could ever produce — account/
 * management/model agreement fields, one-off deposit, transfer authorization,
 * withdrawal, manual buy/sell/rebalance lines, recurring savings and
 * recurring withdrawal — always, regardless of whether the underlying amount
 * or toggle is set. Empty/zero values are left in as-is; which of these are
 * actually relevant for production is decided by the backend.
 *
 * `manualOrderLines` and `savingsFunds` are nested rather than flattened
 * because both use numbered keys (`sec1`/`amount1` vs `fund1Name`/`amount1`)
 * that would otherwise collide once merged into a single object.
 */
function collectOrderVariables(
  doc: DocProducerDocument,
  baseParams: Record<string, string>,
  faModels: QueryModelPosition[],
  faSec: QuerySecurity[]
): Record<string, unknown> {
  const raw = doc.data as Record<string, unknown>;
  const newData = (raw?.newData ?? raw) as Record<string, unknown>;
  const existData = (raw?.existData ?? {}) as Record<string, unknown>;

  const portId = cleanVal(newData.dpPortfolioId);
  const portName = cleanVal(
    newData.dpPortfolioName || existData.dpPortfolioName || "Portefølje"
  );
  const portShortName = cleanVal(
    existData.dpPortfolioShortName || newData.dpPortfolioShortName || ""
  );
  const portType = cleanVal(newData.dpPortType || existData.dpPortType || "");
  const modelType = cleanVal(
    newData.dpModelType || existData.dpModelType || "NOMODEL"
  );
  const existingModel = existData.dpModelType || "NOMODEL";

  const isExisting =
    portId !== "NEW" &&
    portId !== "" &&
    existData.isExisting !== false &&
    cleanVal(existData.isExisting) !== "false";

  const newFee = dd(newData.dpManagementFee);
  const existingFee =
    existData.dpManagementFee === undefined
      ? newFee
      : dd(existData.dpManagementFee);

  const dpAmountToInvest = dd(newData.dpAmountToInvest);
  const dpSumAvCash = dd(existData.dpSumAvCash ?? newData.dpSumAvCash);
  const dpSumInsValue = dd(existData.dpSumInsValue ?? newData.dpSumInsValue);
  const dpOnOffAmount = dd(newData.dpOnOffAmount);
  const dpWithdrawal = dd(newData.dpWithdrawal);
  const dpOnOffDate = cleanVal(newData.dpOnOffDate);
  const dpAccount = fmtAccount(
    cleanVal(newData.dpAccount || newData.dpNewAccount || "")
  );
  const dpIpsWithdrawalAge = dd(newData.dpIpsWithdrawalAge);
  const dpIpsAnnualSavings = dd(newData.dpIpsAnnualSavings);

  const moveFrom = cleanVal(newData.moveFrom || existData.moveFrom || "");
  const moveFromId = cleanVal(newData.moveFromId || existData.moveFromId || "");
  const askToASk = cleanVal(newData.askToAsk || existData.askToAsk || "");
  const moveAll = cleanVal(newData.moveAll || existData.moveAll || "");

  const buyOrders = (newData.buyOrders as BuyOrderRow[]) || [];
  const sellOrders = (newData.sellOrders as SellOrderRow[]) || [];

  const portAmount = dpSumAvCash + dpSumInsValue + dpAmountToInvest;
  const isCap = ["20", "21", "22", "23", "24"].includes(portType);
  const isModel = modelType !== "NOMODEL" && modelType !== "";

  const portTypeLabels: Record<string, string> = {
    "20": "Fondskonto",
    "21": "VPS-konto",
    "22": "ASK",
    "23": "IPS",
    "24": "IPS",
  };
  const displayPortName = !isExisting
    ? `${portTypeLabels[portType] ?? "Portefølje"} Ny`
    : portName;
  const portIdLabel =
    isExisting && portShortName
      ? `${portTypeLabels[portType] ?? "Portefølje"} - ${portShortName}`
      : displayPortName;

  const variables: Record<string, unknown> = {
    portId: displayPortName,
    portIdLabel,
    portName,
    portShortName,
    portType,
    portAccount: dpAccount,
    modelType,
    existingModel,
    isExisting,
    dpServiceType: cleanVal(newData.dpServiceType),
    serviceType: SERVICE_TYPE_LABELS[cleanVal(newData.dpServiceType)] ?? "",
    portIpsage: portType === "23" ? String(dpIpsWithdrawalAge) : "",
    portIpsAmount: portType === "23" ? `${dfzero(dpIpsAnnualSavings)} kr` : "",
  };

  // ── Account / management / model agreement fields ────────────────────────
  variables.actMandat = getModelDescription(modelType);
  variables.actAmount =
    dfzero(portAmount) + " kr" + `, nytt volum: ${dfzero(dpAmountToInvest)} kr`;
  variables.actDesc =
    faModels.find((p) => p.short_name === modelType)?.description ??
    getModelDescription(modelType);
  variables.actRisk = getModelRisk(modelType);
  variables.portAmount = dfzero(portAmount) + " kr";
  variables.portCostType = isCap ? "Capguardhonoraret" : "Honorar";

  const feeCtx: FeeContext = {
    agreedFee: 0,
    accountFee: isCap ? 0.15 : 0.3,
    totCost: 0,
    manFee: 0,
    kickback: 0,
  };
  const feeVariables: Record<string, string> = {};
  setFeesAsParam(
    feeVariables,
    faModels,
    isModel ? modelType : "",
    newFee,
    portAmount,
    feeCtx,
    isCap,
    portType,
    existingFee
  );
  Object.assign(variables, feeVariables);

  // ── One-off deposit ───────────────────────────────────────────────────────
  const descPortfolio =
    displayPortName + " " + (isExisting ? portShortName : "");
  const descOrderType = isModel
    ? getModelDescription(modelType)
    : "Manuell ordre";
  variables.dpOnOffAmount = dpOnOffAmount;
  variables.dpOnOffDate = dpOnOffDate;
  variables.onOffAmt = dfzero(dpOnOffAmount) + " kr";
  variables.onOffDesc = descPortfolio + "\n" + descOrderType;
  variables.portOnOffDate = dpOnOffDate || "";

  // ── Transfer authorization (move from another provider) ──────────────────
  variables.moveFrom = moveFrom;
  variables.moveFromId = moveFromId;
  variables.moveAskType = askToASk
    ? "Fra Aksjesparekonto til Aksjesparekonto"
    : "Fra Verdipapirkonto til Aksjesparekonto";
  variables.movePart = moveAll
    ? "Hele min beholdning"
    : "Deler av min beholdning";
  variables.moveAskport = !isExisting ? "Under opprettelse" : portShortName;
  for (let i = 1; i < 10; i++) {
    variables[`Instrument${i}`] = cleanVal(
      newData[`Instrument${i}`] || existData[`Instrument${i}`] || ""
    );
    variables[`InstrumentAmt${i}`] = cleanVal(
      newData[`InstrumentAmt${i}`] || existData[`InstrumentAmt${i}`] || ""
    );
  }

  // ── Withdrawal (one-off) ──────────────────────────────────────────────────
  variables.wdAmount = dfzero(dpWithdrawal) + " kr";
  variables.wdAcc = dpAccount;

  // ── Manual buy / sell / rebalance order lines (nested — numbered keys) ───
  // Only populated when there actually are manual buy/sell rows — a model
  // order with no manual rows has nothing to list here (mirrors the
  // `hasBuyOrders || hasSellOrders` gate the PDF-mapping logic used).
  const hasBuyOrders = buyOrders.some(
    (o) => o.securityId !== null && o.securityId !== 0
  );
  const hasSellOrders = sellOrders.some((o) => o.securityId !== 0);
  const manualOrderLines: Record<string, string> = {};
  if (hasBuyOrders || hasSellOrders) {
    let count = 1;
    for (const order of buyOrders) {
      if (!order.securityId) continue;
      const secDet = faSec.find((s) => s.id === order.securityId);
      const secCode =
        secDet?.isin_code ?? order.isinCode ?? order.instrumentCode;
      manualOrderLines[`sec${count}`] = `${order.instrumentName}\n${secCode}`;
      manualOrderLines[`type${count}`] = "Kjøp";
      manualOrderLines[`mt${count}`] =
        order.type === "Limit" ? `Limit ${order.limitPrice}` : "Market";
      if (order.type === "Limit" && /^\d+$/.test(order.duration)) {
        manualOrderLines[`omarketDur${count}`] = order.duration;
      }
      if (dd(order.amountInput) > 0) {
        manualOrderLines[`totAmount${count}`] =
          dfzero(dd(order.amountInput)) + " kr";
      } else if (dd(order.quantity) > 0) {
        manualOrderLines[`amount${count}`] =
          dfzero(dd(order.quantity)) + " stk";
      }
      count++;
    }
    for (const order of sellOrders) {
      if (!order.securityId) continue;
      manualOrderLines[`sec${count}`] = `${order.name}\n${order.securityCode}`;
      manualOrderLines[`type${count}`] = "Salg";
      manualOrderLines[`mt${count}`] =
        order.type === "Limit" ? `Limit ${order.limitPrice}` : "Market";
      if (order.type === "Limit" && /^\d+$/.test(order.duration)) {
        manualOrderLines[`omarketDur${count}`] = order.duration;
      }
      manualOrderLines[`amount${count}`] = dfzero(dd(order.sellQty)) + " stk";
      count++;
    }
    if (isModel) {
      const modelPositions = faModels.filter((p) => p.short_name === modelType);
      if (
        modelType === "CAPGUARDEKB" ||
        modelType === "CAPGUARDEIENDOM" ||
        modelType === "FONDSOBLIGASJON" ||
        modelPositions.length <= 1
      ) {
        const desc = getModelDescription(modelType);
        manualOrderLines[`sec${count}`] = `${desc}\n${modelType}`;
        manualOrderLines[`type${count}`] = "Kjøp";
        manualOrderLines[`mt${count}`] = "Rebalansering";
        manualOrderLines[`totAmount${count}`] = dfzero(portAmount) + " kr";
        count++;
      } else {
        for (const pos of modelPositions) {
          const posAmount = portAmount * (pos.share_of_portfolio / 100);
          const posName = pos.name ?? pos.description ?? pos.short_name;
          const posCode = pos.isin_code ?? pos.security_code ?? pos.short_name;
          manualOrderLines[`sec${count}`] = `${posName}\n${posCode}`;
          manualOrderLines[`type${count}`] = "Kjøp";
          manualOrderLines[`mt${count}`] = "Rebalansering";
          manualOrderLines[`totAmount${count}`] = dfzero(posAmount) + " kr";
          count++;
        }
      }
    }
  }
  variables.manualOrderLines = manualOrderLines;
  variables.portDynamicSell = newData.portDynamicSell === true;
  variables.portDynamicWd = newData.portDynamicWd === true;

  // ── Recurring savings agreement (nested — numbered keys) ─────────────────
  const dpSavingsEnabled = newData.dpSavingsEnabled === true;
  const dpSavingsAmount = dd(newData.dpSavingsAmount);
  const dpSavingsDayOfMonth = cleanVal(newData.dpSavingsDayOfMonth);
  const dpSavingsAvtalegiro = newData.dpSavingsAvtalegiro === true;
  const dpSavingsDayNum = dd(newData.dpSavingsDayOfMonth);

  variables.dpSavingsEnabled = dpSavingsEnabled;
  variables.savAmount = dfzero(dpSavingsAmount) + " kr";
  variables.savDate = dpSavingsDayOfMonth;
  variables.savAG = dpSavingsAvtalegiro ? "Ja" : "Nei";
  variables.savOrderDate = isModel
    ? "Handles i henhold til mandat"
    : dpSavingsDayNum < 9 || dpSavingsDayNum > 24
    ? "10"
    : "25";

  const dpSavingsFunds =
    (newData.dpSavingsFunds as Array<{ name: string; pct: number }>) || [];
  const savingsFunds: Record<string, string> = {};
  for (let n = 1; n <= 5; n++) {
    const fund = dpSavingsFunds[n - 1];
    if (fund && !isModel) {
      const pct = dd(fund.pct);
      const fundKr = Math.round((pct / 100) * dpSavingsAmount);
      savingsFunds[`fund${n}Name`] = cleanVal(fund.name);
      savingsFunds[`amount${n}`] =
        "kr " + dfzero(fundKr) + " (" + dfzero(pct) + " %)";
    } else {
      savingsFunds[`fund${n}Name`] = "";
      savingsFunds[`amount${n}`] = "";
    }
  }
  variables.savingsFunds = savingsFunds;

  // ── Recurring withdrawal agreement ───────────────────────────────────────
  const dpWithdrawalEnabled = newData.dpWithdrawalEnabled === true;
  const dpWdAmount = dd(newData.dpWdAmount);
  const dpWdDayOfMonth = cleanVal(newData.dpWdDayOfMonth);
  const dpWdAccount = fmtAccount(cleanVal(newData.dpWdAccount || ""));

  variables.dpWithdrawalEnabled = dpWithdrawalEnabled;
  variables.portWdAccount = dpWdAccount;
  variables.portFixedWdDate = dpWdDayOfMonth;
  variables.portFixedWdAmount = dfzero(dpWdAmount) + " kr";

  return { ...baseParams, ...variables };
}

// ---------------------------------------------------------------------------
// Generic (non-order) variable collection — spreads every primitive field,
// no gating on which fields "count".
// ---------------------------------------------------------------------------

function collectGenericVariables(
  doc: DocProducerDocument,
  baseParams: Record<string, string>
): Record<string, unknown> {
  const raw = doc.data as Record<string, unknown>;
  const data = (raw?.newData ?? raw) as Record<string, unknown>;
  const variables: Record<string, unknown> = { ...baseParams };
  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined) continue;
    variables[key] = value;
  }
  return variables;
}

function isProfileDocument(doc: DocProducerDocument): boolean {
  return (
    doc.docType === "kundeprofil" ||
    doc.docType === "mifidForm" ||
    doc.docType === "amlForm" ||
    doc.docType === "documentFromSecurity"
  );
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

/**
 * Translates the queued DocProducer documents into one entry per document —
 * unlike `buildDocumentProductionList`, this never explodes a single "order"
 * document into several production-ready sub-documents (account agreement,
 * one-off deposit, transfer, savings, ...). Every variable that could ever be
 * relevant for that document is always collected, regardless of whether the
 * underlying amount/toggle is actually set — deciding which documents to
 * actually produce is the backend's responsibility now.
 *
 * Intentionally does not call `buildDocumentProductionList` — it only reuses
 * the shared, pure formatting/fee helpers from that module.
 */
export function buildDocumentListWithVariables(
  contact: Contact,
  avdData: Contact | null | undefined,
  documents: DocProducerDocument[],
  faModels: QueryModelPosition[],
  faSec: QuerySecurity[],
  // Kept for signature parity with buildDocumentProductionList; not used —
  // the backend now owns the logic that depends on these.
  _activePorts: Portfolio[],
  _pendingOrders: PendingOrder[],
  docProdOut: Record<string, unknown> | null = null
): DocumentListWithVariables {
  const baseParams = getStdParams(contact, avdData);

  return {
    standardParams: baseParams,
    documents: documents.map((doc) => {
      const variables =
        doc.docType === "order"
          ? collectOrderVariables(doc, baseParams, faModels, faSec)
          : collectGenericVariables(doc, baseParams);

      if (isProfileDocument(doc)) {
        if (docProdOut) variables.docProdOut = docProdOut;
      }

      return {
        id: doc.id,
        type: doc.docType,
        data: variables,
      };
    }),
  };
}
